/* ============================================================
   L'Epoque — Subscribe widget enhancement (C1 v3)
   (Shopify Subscriptions app block)

   Side-by-side cards: One-Time Purchase | Subscribe (expands with
   benefits, exact price, and a frequency dropdown when selected).

   v3 architecture: the Shopify Subscriptions app re-renders its
   inner markup after load, which detaches naively-bound listeners.
   All events are therefore DELEGATED from the container, and every
   handler queries the DOM fresh — nothing holds references to
   nodes the app may replace. All mutations are idempotent and
   re-applied by a debounced observer after any app re-render.

   The price line computes the true discounted price (e.g. $112.50)
   from the one-time price and the plan's percentage, showing cents
   only when they exist — the app's own display rounds to whole
   dollars per the store money format.

   Failure mode: if selectors stop matching, functions no-op and
   the widget renders as the app ships it. Nothing here throws.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- fresh-DOM helpers (no cached node refs) ---------- */

  function getContainer() {
    return document.querySelector('.shopify_subscriptions_app_container');
  }
  function getSubGroup(container) {
    var groups = container.querySelectorAll('.subscription_group');
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('ul.group_list')) return groups[i];
    }
    return null;
  }
  function getOneTimeGroup(container) {
    var groups = container.querySelectorAll('.subscription_group');
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('input.one_time_purchase_option_app_block')) {
        return groups[i];
      }
    }
    return null;
  }
  function getSubRadios(subGroup) {
    return Array.prototype.slice.call(
      subGroup.querySelectorAll('ul.group_list input[type="radio"]')
    );
  }
  function parseRow(radio) {
    var rowEl = radio.closest('label') || radio.closest('li');
    if (!rowEl) return null;
    var txt = (rowEl.textContent || '').trim().replace(/\s+/g, ' ');
    var m = txt.match(/Deliver every (.+?),\s*(\d+(?:\.\d+)?)% off/i);
    return {
      row: rowEl,
      label: m ? m[1] : txt.replace(/\$\d[\d.,]*/g, '').trim(),
      pct: m ? parseFloat(m[2]) : null
    };
  }

  /* Compute the exact discounted price from the one-time price.
     "$125" at 10% -> "$112.50"; whole results stay whole. */
  function computePrice(container, pct) {
    var otPrice = null;
    var oneTimeGroup = getOneTimeGroup(container);
    if (oneTimeGroup) {
      otPrice = oneTimeGroup.querySelector('.in_widget_price');
    }
    if (!otPrice || pct == null) return null;
    var m = (otPrice.textContent || '').trim().match(/^([^0-9]*)([\d.,]+)(.*)$/);
    if (!m) return null;
    var base = parseFloat(m[2].replace(/,/g, ''));
    if (isNaN(base)) return null;
    var val = Math.round(base * (1 - pct / 100) * 100) / 100;
    var text = (val % 1 === 0) ? String(val) : val.toFixed(2);
    return m[1] + text + m[3];
  }

  /* ---------- idempotent build ---------- */

  function ensureBuilt(container) {
    var block = container.querySelector('.shopify_subscriptions_app_block');
    if (!block) return false;
    var planCard = block.querySelector('.plan_card');
    if (!planCard) return false;
    var subGroup = getSubGroup(container);
    if (!subGroup) return false;
    var radios = getSubRadios(subGroup);
    if (!radios.length) return false;

    container.classList.add('lepoque-two-radio');

    var oneTimeGroup = getOneTimeGroup(container);
    if (oneTimeGroup) {
      oneTimeGroup.classList.add('lepoque-onetime-card');
      var otWalker = document.createTreeWalker(oneTimeGroup, NodeFilter.SHOW_TEXT);
      var otNode;
      while ((otNode = otWalker.nextNode())) {
        if (/one-time purchase/i.test(otNode.textContent) &&
            otNode.textContent.indexOf('One-Time Purchase') === -1) {
          otNode.textContent = otNode.textContent.replace(
            /one-time purchase/i, 'One-Time Purchase'
          );
        }
      }
    }

    // Heading (once)
    if (!block.querySelector('.lepoque-choice-heading')) {
      var heading = document.createElement('div');
      heading.className = 'lepoque-choice-heading';
      heading.textContent = 'Choose Your Frequency';
      planCard.parentNode.insertBefore(heading, planCard);
    }

    // Subscribe card internals (once each)
    if (!subGroup.querySelector('.lepoque-sub-proxy')) {
      var firstRow = parseRow(radios[0]);
      var labelPct = firstRow && firstRow.pct != null ? firstRow.pct + '%' : '10%';
      var proxy = document.createElement('div');
      proxy.className = 'lepoque-sub-proxy';
      proxy.setAttribute('role', 'radio');
      proxy.setAttribute('tabindex', '0');
      proxy.setAttribute('aria-checked', 'false');
      var drawnRadio = document.createElement('span');
      drawnRadio.className = 'lepoque-sub-radio';
      drawnRadio.setAttribute('aria-hidden', 'true');
      var labelSpan = document.createElement('span');
      labelSpan.className = 'lepoque-sub-label';
      labelSpan.textContent = 'Subscribe \u2014 Save ' + labelPct;
      proxy.appendChild(drawnRadio);
      proxy.appendChild(labelSpan);
      subGroup.insertBefore(proxy, subGroup.firstChild);
    }

    if (!subGroup.querySelector('.lepoque-sub-perks')) {
      var perkPctRow = parseRow(radios[0]);
      var perkPct = perkPctRow && perkPctRow.pct != null ? perkPctRow.pct + '%' : '10%';
      var perks = document.createElement('ul');
      perks.className = 'lepoque-sub-perks';
      var perkTexts = [
        'Save ' + perkPct + ' on every order.',
        'Pause, skip, or cancel anytime.'
      ];
      for (var p = 0; p < perkTexts.length; p++) {
        var li = document.createElement('li');
        li.textContent = perkTexts[p];
        perks.appendChild(li);
      }
      subGroup.appendChild(perks);
    }

    if (!subGroup.querySelector('.lepoque-sub-price')) {
      var priceLine = document.createElement('div');
      priceLine.className = 'lepoque-sub-price';
      subGroup.appendChild(priceLine);
    }

    if (!subGroup.querySelector('.lepoque-freq-row')) {
      var freqRow = document.createElement('div');
      freqRow.className = 'lepoque-freq-row';
      var select = document.createElement('select');
      select.className = 'lepoque-freq-select';
      select.setAttribute('aria-label', 'Delivery frequency');
      freqRow.appendChild(select);
      subGroup.appendChild(freqRow);
    }

    // (Re)build dropdown options if the radio set changed
    var select2 = subGroup.querySelector('.lepoque-freq-select');
    if (select2 && select2.options.length !== radios.length) {
      select2.innerHTML = '';
      for (var o = 0; o < radios.length; o++) {
        var info = parseRow(radios[o]);
        var opt = document.createElement('option');
        opt.value = String(o);
        opt.textContent = 'Every ' + (info ? info.label : (o + 1) + ' months');
        select2.appendChild(opt);
      }
    }

    return true;
  }

  /* ---------- state sync (fresh queries every time) ---------- */

  function update() {
    var container = getContainer();
    if (!container) return;
    if (!ensureBuilt(container)) return;

    var subGroup = getSubGroup(container);
    var radios = getSubRadios(subGroup);
    var proxy = subGroup.querySelector('.lepoque-sub-proxy');
    var select = subGroup.querySelector('.lepoque-freq-select');
    var priceLine = subGroup.querySelector('.lepoque-sub-price');

    var idx = -1;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked) { idx = i; break; }
    }
    var on = idx !== -1;

    if (proxy) proxy.setAttribute('aria-checked', on ? 'true' : 'false');
    subGroup.classList.toggle('lepoque-open', on);

    if (on) {
      if (select) select.value = String(idx);
      var info = parseRow(radios[idx]);
      var exact = computePrice(container, info ? info.pct : null);
      if (priceLine) {
        if (!exact) {
          var appPrice = info && info.row.querySelector('.in_widget_price');
          exact = appPrice ? appPrice.textContent.trim() : '';
        }
        priceLine.textContent = exact ? exact + ' per delivery' : '';
      }
    } else if (priceLine) {
      priceLine.textContent = '';
    }
  }

  var activating = false;
  function activate() {
    if (activating) return;
    activating = true;
    try {
      var container = getContainer();
      if (!container) return;
      var subGroup = getSubGroup(container);
      if (!subGroup) return;
      var radios = getSubRadios(subGroup);
      if (!radios.length) return;
      var anyChecked = false;
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) { anyChecked = true; break; }
      }
      if (!anyChecked) {
        var select = subGroup.querySelector('.lepoque-freq-select');
        var target = select ? (parseInt(select.value, 10) || 0) : 0;
        if (radios[target]) radios[target].click();
      }
      update();
    } finally {
      activating = false;
    }
  }

  /* ---------- delegated events (document, CAPTURE phase) ----------
     The app/theme stops propagation on these events, so bubble-phase
     delegation never fires. Capture-phase listeners run before any
     stopPropagation can block them — same technique as the cart's
     remove-button listener. ---------- */

  var bound = false;
  function bindOnce() {
    if (bound) return;
    bound = true;

    // Real user clicks on the subscribe card
    document.addEventListener('click', function (e) {
      if (!e.isTrusted) return;
      var container = getContainer();
      if (!container || !container.contains(e.target)) return;
      var group = e.target.closest('.subscription_group');
      if (!group || !group.querySelector('ul.group_list')) return;
      if (e.target.closest('.lepoque-freq-select')) return;
      activate();
    }, true);

    // Keyboard on the proxy
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (!e.target.classList || !e.target.classList.contains('lepoque-sub-proxy')) return;
      e.preventDefault();
      activate();
    }, true);

    // Frequency dropdown
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t.classList && t.classList.contains('lepoque-freq-select')) {
        var container = getContainer();
        if (!container) return;
        var subGroup = getSubGroup(container);
        if (!subGroup) return;
        var radios = getSubRadios(subGroup);
        var target = parseInt(t.value, 10);
        if (radios[target]) radios[target].click();
        update();
        return;
      }
      // Any purchase-option radio change (app's or ours)
      if (t.matches && t.matches('.shopify_subscriptions_app_block input[type="radio"]')) {
        update();
      }
    }, true);
  }

  /* ---------- boot + resilience to app re-renders ---------- */

  function boot() {
    bindOnce();
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      update();
    }, 150);
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();